<template>
  <div>
    <JoszakiAutocomplete
      :value="value"
      v-bind="$attrs"
      :pill-type="pillType"
      exclude-selected
      disable-sort
      :icon="icon"
      :items="filteredZones"
      :formatter="formatter"
      :loading="loading"
      @search="onSearch"
      @focus="onFocus"
      @scroll-end="onScrollEnd"
      @input="$emit('input', $event)"
      v-on="$listeners"
    >
      <template v-if="isMapSelectorEnabled" #topActions>
        <div
          class="flex items-center gap-2 pl-3 pr-2 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 text-sm border-b border-gray-200 dark:border-gray-600 font-medium text-black dark:text-gray-100"
          @click.stop.prevent="openMapSelector"
          @mousedown.prevent
        >
          <IconComponent icon="map-marked-alt" />
          <span>{{ $t("conductor.zones.mapSelector.openButton") }}</span>
        </div>
      </template>
      <template #empty>
        {{ $t("conductor.common.noResults") }}
      </template>
      <template #footer>
        <div
          v-if="filteredZones.length && !hasMore"
          class="py-2 text-center text-xs text-gray-400 dark:text-gray-500"
        >
          {{ $t("conductor.common.noMoreItems") }}
        </div>
      </template>
    </JoszakiAutocomplete>
    <ZoneMapSelectorModal
      v-if="isMapSelectorEnabled"
      :open="isMapSelectorOpen"
      :existing-zones="existingZones"
      :reference-zones="referenceZones"
      :reference-color-type="referenceColorType"
      :reference-selectable="referenceSelectable"
      :reference-hint-key="referenceHintKey"
      :entity-label="entityLabel"
      :color-type="pillType"
      :person-city="personCity"
      @close="isMapSelectorOpen = false"
      @confirm="onMapConfirm"
    />
  </div>
</template>

<script>
import { computed, ref } from "@nuxtjs/composition-api";
import ZoneMapSelectorModal from "~/components/_refactored/autocomplete/ZoneMapSelectorModal.vue";
import { useZoneActions } from "~/components/_refactored/conductor/zones/useZoneActions";

const PAGE_SIZE = 50;

export default {
  components: {
    ZoneMapSelectorModal,
  },
  props: {
    value: {
      type: [Object, Array],
      default: null,
    },
    icon: {
      type: String,
      default: "map-marked-alt",
    },
    itemsMapper: {
      type: Function,
      default: (item) => item,
    },
    formatter: {
      type: Function,
      default: (item) => item.fullName,
    },
    isMapSelectorEnabled: {
      type: Boolean,
      default: false,
    },
    // color used for the pills and for the existing/selected zones on the map
    // selector, kept in sync so the map matches the autocomplete (e.g. "rating"
    // yellow for waiting lists, "success" green for active zones)
    pillType: {
      type: String,
      default: "primary",
    },
    // zones from the other list shown for context on the map selector (e.g. the
    // person's active zones on the waiting-list map, or vice versa)
    referenceZones: {
      type: Array,
      default: () => [],
    },
    // color of the reference zones — matches the field they belong to
    referenceColorType: {
      type: String,
      default: "success",
    },
    // whether reference zones can be picked on the map (false = read-only)
    referenceSelectable: {
      type: Boolean,
      default: false,
    },
    // i18n key for the reference zone tooltip hint
    referenceHintKey: {
      type: String,
      default: "conductor.zones.mapSelector.referenceHint",
    },
    // lowercase plural noun for the zones in this context, shown in the modal's
    // current/adding/removing section labels (defaults to "zones")
    entityLabel: {
      type: String,
      default: "",
    },
    // DataTable filters forwarded to the zones query, e.g. to restrict the
    // results to zones with a public (current) map version
    filters: {
      type: Array,
      default: () => [],
    },
    // the edited person's city, forwarded to the map selector as a pin
    personCity: {
      type: Object,
      default: null,
    },
  },
  setup(props, { emit }) {
    const { getZones } = useZoneActions();
    const filteredZones = ref([]);
    const loading = ref(false);
    const isMapSelectorOpen = ref(false);
    const currentPage = ref(1);
    const currentSearch = ref("");
    const hasMore = ref(false);

    const fetchZones = async ({ search, page, append }) => {
      loading.value = true;
      try {
        const { items, pagination } = await getZones({
          search,
          page,
          pageSize: PAGE_SIZE,
          orderBy: "fullName",
          order: "asc",
          filters: props.filters,
        });
        const mappedItems = items.map(props.itemsMapper);
        filteredZones.value = append
          ? [...filteredZones.value, ...mappedItems]
          : mappedItems;
        currentPage.value = page;
        currentSearch.value = search;
        hasMore.value = pagination?.hasNext ?? false;
      } finally {
        loading.value = false;
      }
    };

    const onSearch = (search) => {
      fetchZones({ search, page: 1, append: false });
    };

    const onScrollEnd = () => {
      if (loading.value || !hasMore.value) {
        return;
      }
      fetchZones({
        search: currentSearch.value,
        page: currentPage.value + 1,
        append: true,
      });
    };

    const onFocus = (search) => {
      if (!filteredZones.value.length) {
        onSearch(search ?? "");
      }
      emit("focus", search);
    };

    const openMapSelector = () => {
      isMapSelectorOpen.value = true;
    };

    const valueAsArray = computed(() => {
      return Array.isArray(props.value)
        ? props.value
        : props.value
        ? [props.value]
        : [];
    });

    const existingZones = computed(() => {
      return valueAsArray.value
        .map((item) => item?.zone ?? item)
        .filter((zone) => zone?.id != null);
    });

    const onMapConfirm = ({ addedZones, removedZones }) => {
      if (removedZones.length) {
        const removedZoneIds = removedZones.map((zone) => zone.id);
        const removedItems = valueAsArray.value.filter((item) =>
          removedZoneIds.includes(item?.zone?.id ?? item?.id)
        );
        const newValue = valueAsArray.value.filter(
          (item) => !removedItems.includes(item)
        );
        emit("input", Array.isArray(props.value) ? newValue : null);
        emit("mapRemove", removedItems);
      }
      if (addedZones.length) {
        const wrappedZones = addedZones.map((zone) => props.itemsMapper(zone));
        emit("mapSelect", wrappedZones);
      }
      isMapSelectorOpen.value = false;
    };

    return {
      filteredZones,
      onSearch,
      onScrollEnd,
      onFocus,
      loading,
      hasMore,
      isMapSelectorOpen,
      openMapSelector,
      onMapConfirm,
      existingZones,
    };
  },
};
</script>
